import { Component } from '@theme/component';
import { hasInteracted, whenInteracted } from '@theme/first-interaction';

/**
 * How far outside the viewport a video starts loading. Applied to every side, so
 * that a horizontally scrolled slideshow slide buffers before it snaps into place.
 */
const ROOT_MARGIN = '300px';

/** @type {IntersectionObserver | null} */
let sharedObserver = null;

/**
 * Nothing about a video — not a byte, not a `src` attribute — happens before the
 * visitor has interacted with the page. That decision now lives in
 * `@theme/first-interaction`, shared with every other deferred-media surface in
 * the theme so they cannot drift apart.
 */

/**
 * One observer for every video on the page, built on first use so that a browser
 * without IntersectionObserver never constructs it.
 *
 * @returns {IntersectionObserver}
 */
function getSharedObserver() {
  return (sharedObserver ??= new IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (!isIntersecting) continue;

        const component = target.closest('video-background-component');

        if (component instanceof VideoBackgroundComponent) component.loadSources();
      }
    },
    { rootMargin: ROOT_MARGIN }
  ));
}

/**
 * Connections where a decorative background video is not worth the bytes, even
 * with Data Saver switched off. `effectiveType` is round-trip-time based, so it
 * also catches a nominally fast radio that is actually congested.
 */
const SLOW_CONNECTIONS = new Set(['slow-2g', '2g']);

/**
 * Events that every engine counts as a user activation: a `play()` made
 * synchronously inside one of these handlers may start media that autoplay
 * policy would otherwise refuse. The events that usually open the
 * first-interaction gate — `mousemove`, `scroll`, `touchstart` — do not qualify,
 * which is why the gate alone cannot be relied on (see `#play`).
 */
const ACTIVATION_EVENTS = ['mousedown', 'keydown', 'touchend', 'click'];

/**
 * Whether this visitor has asked us not to spend their data or their attention,
 * or is on a connection too slow to spend it on. Read per video as it comes into
 * view rather than once at startup, so that a setting changed — or a connection
 * that degrades — part-way down the page is honoured by the videos below it.
 *
 * @returns {boolean}
 */
function prefersNoVideo() {
  const { connection } = navigator;

  return (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    connection?.saveData === true ||
    SLOW_CONNECTIONS.has(connection?.effectiveType)
  );
}

/**
 * A custom element that renders a video background.
 *
 * Sources are authored as `data-video-source` rather than `src`, so the browser
 * has nothing to fetch while the document is parsing. They are promoted to real
 * `src` attributes only once the visitor has interacted with the page, the element
 * is on screen, and the main thread is idle — in that order. This matters because
 * `autoplay` overrides `preload="none"` — Chrome buffers the whole file to keep
 * playback going — so withholding the `src` is the only way to keep a large
 * background video off the first-paint path.
 *
 * The <video> carries `data-video-state` through `loading`, then `loaded` or
 * `failed`. Callers hand their poster over to the video off that attribute, and
 * it doubles as the guard against loading twice.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {number | null} */
  #idleHandle = null;

  /** Unsubscribes from the first-interaction gate. @type {(() => void) | null} */
  #releaseInteraction = null;

  /** Tears down the activation listeners armed by a refused `play()`. @type {AbortController | null} */
  #retry = null;

  connectedCallback() {
    super.connectedCallback();

    // Observe the <video>, never `this`. A host styled `display: contents` — as
    // the hero and the slideshow slides are, so the <video> stays the direct
    // layout child of the media wrapper — generates no box, so IntersectionObserver
    // would report a 0x0 rect and never fire, leaving the sources unattached.
    const target = this.refs.videoElement;

    if (!target) return;

    // Merchants need to see real video while they edit, and a browser with no
    // IntersectionObserver has no way to tell us when the video comes into view.
    if (typeof IntersectionObserver === 'undefined' || window.Shopify?.designMode) {
      this.loadSources();
      return;
    }

    getSharedObserver().observe(target);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#releaseInteraction?.();
    this.#releaseInteraction = null;
    this.#cancelRetry();

    const { videoElement } = this.refs;

    if (videoElement) {
      sharedObserver?.unobserve(videoElement);

      // Release a load that never got past the idle queue, so that the same node
      // reconnected elsewhere is not stuck behind its own guard.
      if (videoElement.dataset.videoState === 'loading') delete videoElement.dataset.videoState;
    }

    this.#cancelIdle();
  }

  /**
   * Promotes every `data-video-source` to a real `src` and starts playback.
   *
   * Safe to call any number of times: `data-video-state` on the <video> is the
   * guard, so it holds across re-initialization rather than living on this instance.
   */
  loadSources() {
    const { videoElement } = this.refs;

    if (!videoElement || videoElement.dataset.videoState) return;

    // On screen, but the visitor has not touched the page yet. Stay silent and
    // ask to be called back; `data-video-state` guards the re-entry.
    if (!hasInteracted()) {
      this.#releaseInteraction?.();
      this.#releaseInteraction = whenInteracted(() => this.loadSources());
      return;
    }

    sharedObserver?.unobserve(videoElement);

    // Leave the poster in place. Nothing is fetched and nothing moves.
    if (prefersNoVideo()) {
      videoElement.dataset.videoState = 'skipped';
      return;
    }

    videoElement.dataset.videoState = 'loading';
    this.#whenIdle(() => this.#attachSources());
  }

  #cancelIdle() {
    if (this.#idleHandle === null) return;

    if (typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(this.#idleHandle);
    } else {
      window.clearTimeout(this.#idleHandle);
    }

    this.#idleHandle = null;
  }

  /**
   * Runs `callback` on the next idle period, falling back to a short timeout
   * where `requestIdleCallback` is unavailable (Safari).
   * @param {() => void} callback
   */
  #whenIdle(callback) {
    const run = () => {
      this.#idleHandle = null;
      callback();
    };

    this.#idleHandle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(run, { timeout: 2000 })
        : window.setTimeout(run, 200);
  }

  #attachSources() {
    const { videoSources, videoElement } = this.refs;

    if (!videoElement) return;

    videoElement.addEventListener('loadeddata', () => (videoElement.dataset.videoState = 'loaded'), { once: true });

    // A broken asset keeps its poster rather than being retried: a retry loop on
    // a file that will not decode costs the visitor bandwidth for nothing.
    videoElement.addEventListener('error', () => (videoElement.dataset.videoState = 'failed'), { once: true });

    for (const source of videoSources ?? []) {
      const { videoSource } = source.dataset;

      if (!videoSource) continue;

      source.setAttribute('src', videoSource);
      delete source.dataset.videoSource;
    }

    videoElement.load();

    // `autoplay` is normally enough on its own, but the element can be past its
    // autoplay check by the time the sources land.
    if (videoElement.hasAttribute('autoplay')) this.#play();
  }

  /**
   * Starts playback and, if the browser refuses, tries again from inside the
   * visitor's next genuine activation.
   *
   * A muted video is normally allowed to start without a gesture, but Safari
   * withdraws that in Low Power Mode, under thermal pressure, and when the
   * site's Auto-Play preference is "Never Auto-Play": every video `play()` —
   * muted or not — then needs a user gesture, and WebKit draws its own play
   * button over the paused hero. The gate opens on `mousemove`/`scroll`, which
   * are not gestures, and `#whenIdle` strips any gesture there was, so the
   * first attempt is refused with `NotAllowedError`. Swallowing that left the
   * hero frozen for the rest of the visit, however much the visitor clicked.
   *
   * WebKit lifts the restriction for the element for good once `play()` runs
   * synchronously inside a real activation, so one call from the next
   * `mousedown`/`keydown`/`touchend`/`click` is enough. It must stay synchronous:
   * no `await`, no timer, nothing between the event and the call.
   */
  #play() {
    const { videoElement } = this.refs;

    if (!videoElement) return;

    videoElement.play().then(
      () => this.#cancelRetry(),
      (error) => {
        // A later `load()` superseded this attempt (AbortError), or the file
        // will not decode (NotSupportedError — the error listener marks it
        // `failed`). Neither is a policy decision, and a gesture would not
        // change either, so there is nothing to retry.
        if (error?.name !== 'NotAllowedError') return;

        this.#retryOnActivation();
      }
    );
  }

  /**
   * Arms one `#play()` per genuine activation until one succeeds. Listening on
   * `window` in the capture phase means an app handler that stops propagation
   * cannot hide the activation from us, and `isTrusted` keeps the synthetic
   * click Loop's widget dispatches from counting (see `first-interaction.js`).
   */
  #retryOnActivation() {
    if (this.#retry) return;

    this.#retry = new AbortController();

    const options = { capture: true, passive: true, signal: this.#retry.signal };

    /** @param {Event} event */
    const onActivation = (event) => {
      if (event.isTrusted === false) return;

      this.#play();
    };

    for (const type of ACTIVATION_EVENTS) window.addEventListener(type, onActivation, options);
  }

  #cancelRetry() {
    this.#retry?.abort();
    this.#retry = null;
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
